namespace distill.dashboards {

    export let designer: DashboardDesigner;

    export class DashboardDesigner extends appn.designerv2.DesignerBase {
        _datasets: IDatasetMeta[];
        _restrictedDatasets: IDatasetMeta[];
        _aiQueryUrl: string;
        _itemList: any[];
        _editUserUrl: string;
        /** The server's maximum number of global filters per dashboard (passed on init). */
        maxGlobalFilters: number = 5;

        constructor(datasets?: IDatasetMeta[], restrictedDatasets?: IDatasetMeta[], aiQueryUrl?: string, itemList?: any[], editUserUrl?: string) {
            super();

            appn.designerv2.canvas = new GridStackCanvas({
                float: true,
                itemClass: 'component',
                acceptWidgets: true,
                cellHeight: 'initial', // Kills the internal auto-loop, preventing scrollbar thrashing
                resizable: {
                    // All 8 handles: east, south-east, south, south-west, west, north-west, north, north-east
                    handles: 'e, se, s, sw, w, nw, n, ne'
                }
            });
            appn.designerv2.toolPane = new appn.designerv2.ToolPaneBase();

            this._datasets = datasets || [];
            this._restrictedDatasets = restrictedDatasets || [];
            this._aiQueryUrl = aiQueryUrl || appn.emptyString;
            this._itemList = itemList || [];
            this._editUserUrl = editUserUrl || appn.emptyString;
        }

        getItemList() {
            return this._itemList;
        }

        // Life cycle
        init(args: appn.designerv2.IDesignerArgs): void {
            super.init(args);
            designer = this;            // set strong typed designer instance

            this.maxGlobalFilters = args.maxGlobalFilters || this.maxGlobalFilters;
            dataCache.init(1000 * 60 * 60 * 24 * 365); // 1 year cache to disable data removal - We don't auto-refresh designers.
            dataManager.init(args.dataFetchUrl);
        }

        // A restricted dataset can only be bound at load (it is never offered on an unrestricted component),
        // so snapshot those data queries now. They are read-only for the user, and must be restored exactly
        // when the dataset is switched away from and back to.
        protected captureComponentSnapshots(component: IComponent): void {
            const definition = componentManager.getDefinition(component.type);
            if (!definition)
                return;

            componentManager.getDataQueryPropertyNames(definition).forEach(dataQueryPropName => {
                const dataQuery = (component as any)[dataQueryPropName] as IDataQuery | undefined;
                if (dataQuery && getDatasetRefsInDataQuery(dataQuery).some(ref => this.isRestricted(ref.id)))
                    this.snapshotRoot(component.id, dataQueryPropName);
            });
        }

        bindEvents() {
            super.bindEvents();

            dataManager.bindEvents()
            this.bindRenderEvents();

            // Store the true browser window width
            let lastWindowWidth = window.innerWidth;

            window.addEventListener('resize', () => {
                // CRITICAL GUARD: If the window width didn't change, it was just an internal layout shift. Ignore it.
                if (window.innerWidth === lastWindowWidth)
                    return;
                lastWindowWidth = window.innerWidth;

                const canvas = appn.designerv2.canvas as GridStackCanvas;
                if (canvas && canvas._grid && canvas._grid.el) {
                    const gridWidth = canvas._grid.el.clientWidth;
                    const newCellHeight = Math.floor(gridWidth / 12); // We use the default 12 cells when creating a new GridStack canvas

                    // Tell GridStack to update, which safely scales the ECharts
                    if (newCellHeight > 0) {
                        canvas._grid.cellHeight(newCellHeight);
                    }
                }
            });

            // Trigger it once on load so the initial dashboard renders with square widgets
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 150);
        }

        updateGlobalFilters(filters: IDashboardGlobalFilter[]): void {
            if (!this._design)
                return;

            const design = this._design as IDashboardDesign;
            const oldValue = design.globalFilters;
            design.globalFilters = appn.utils.deepClone(filters);
            // Intentionally not marking autosave dirty: global filters persist only when the dashboard is saved.
            eventBus.publish(EVENTS.STATE_CHANGED, { design: this._design });

            // globalFilters is design-level, so it cannot go through updateComponentProperty
            // (whose unchanged-value bail-out breaks the feedback loop). Publish the property
            // update manually so PROPERTY_UPDATED subscribers (the GlobalFiltersEditor and the
            // titlebar's preview toggle) still fire.
            const titleBar = design.components.find(component => component.type === ComponentType.titleBar);
            if (titleBar)
                eventBus.propertyUpdated(titleBar.id, 'globalFilters', design.globalFilters, oldValue);
        }

        createDefaultDesign(name: string): IDashboardDesign {
            const pieChart = componentManager.createComponent(ComponentType.pieChart);
            const defaultChildren: IComponent[] = [];
            if (pieChart) {
                pieChart.gridPosition = { x: 2, y: 1, w: 4, h: 3 };
                defaultChildren.push(pieChart);
            }
            return {
                title: name,
                metadata: [{ activeLanguages: ['eng'] }],
                globalFilters: [],
                components: [
                    { id: appn.utils.newGuid(true), type: ComponentType.titleBar, title: { eng: name } },
                    { id: appn.utils.newGuid(true), type: ComponentType.group, children: defaultChildren }
                ]
            };
        }

        onUpdateComponentProperty(component: IComponent, propName: string, oldValue: any): boolean {
            return true;
        }

        getDatasets(): IDatasetMeta[] {
            return this._datasets;
        }

        // Collects the dataset ids referenced by every component in the live (possibly unsaved) design, so the
        // server can pick up datasets that exist client-side only.
        getDesignDatasetIds(): string[] {
            const design = this.getDesignForSave();
            if (!design)
                return [];

            const datasetIds = new Set<string>();
            const collect = (components: IComponent[]) => {
                components.forEach(component => {
                    getDatasetRefsInComponent(component).forEach(datasetRef => {
                        if (datasetRef.id)
                            datasetIds.add(datasetRef.id);
                    });

                    if (component.children)
                        collect(component.children);
                });
            };

            collect(design.components);
            return Array.from(datasetIds);
        }

        getRestrictedDatasets(): IDatasetMeta[] {
            return this._restrictedDatasets;
        }

        isRestricted(datasetId: string | null | undefined): boolean {
            return !!datasetId && this._restrictedDatasets.some(t => t.id === datasetId);
        }

        bindRenderEvents(){
            eventBus.subscribe(EVENTS.DATA_FETCH_SUCCESS, (result: IDataQueryResponse) => {
                const refreshedComponentIds = new Set<string>();
                result.Views.forEach(datasetResult => {
                    const cacheKey = appn.utils.normalizeIdentifier(datasetResult.Name);
                    const componentId = cacheKey.split('.')[0];

                    if (componentId) {
                        if (datasetResult.Error) {
                            // Retain stale data if the fetch failed, we'll overlay an error or data warning on the chart
                            const cachedResult = dataCache.get<IDataQueryResult>(cacheKey);
                            if (cachedResult && cachedResult.ObjectResult && cachedResult.ObjectResult.length > 0) {
                                // Preserve the stale data so the chart doesn't blank out
                                datasetResult.ObjectResult = cachedResult.ObjectResult;
                                datasetResult.Headers = cachedResult.Headers;
                            }
                        }

                        if (appn.utils.hasValue(datasetResult.Error))
                            console.error(`Could not fetch data for ${cacheKey}`, datasetResult.Error);

                        refreshedComponentIds.add(componentId);
                        dataCache.set(cacheKey, datasetResult);
                    }
                });

                if (!result || !result.Views)
                    return;

                refreshedComponentIds.forEach(id => {
                    const component = designer.getComponent(id);
                    if (!component)
                        return;

                    const definition = componentManager.getDefinition(component.type);
                    if (!definition || !definition.render)
                        return;

                    const element = $(`#${id}`);
                    if (!element.length)
                        return;

                    definition.render(element, component);
                });
            });

            // Also re-render the chart if a non-data property (e.g., style) changes.
            eventBus.subscribeToPropertyUpdates(payload => {
                const component = designer.getComponent(payload.componentId);
                if (!component)
                    return;

                const definition = componentManager.getDefinition(component.type);
                if (!definition || !definition.render)
                    return;

                const dataQueryPropNames = componentManager.getDataQueryPropertyNames(definition);
                // Only re-render if a non-data (e.g., layout/style) property has changed.
                if (dataQueryPropNames.includes(payload.propName))
                    return;

                const element = $(`#${component.id}`);
                if (!element.length)
                    return;

                definition.render(element, component);
            });

            // Hookup designer preview of aiQuery component
            eventBus.subscribe(DASH_EVENTS.AI_QUERY_SEND, async (payload: IAiQuerySendPayload) => {
                try {
                    const response = await fetch(this._aiQueryUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ...payload, datasetIds: this.getDesignDatasetIds() })
                    });

                    if (!response.ok)
                        throw new Error(`HTTP error! status: ${response.status}`);

                    const json = await response.json();
                    eventBus.publish(DASH_EVENTS.AI_QUERY_RESULT, {
                        componentId: payload.component.id,
                        response: json,
                        success: true,
                        upgradeUrl: this._editUserUrl,
                    });
                } catch (error) {
                    eventBus.publish(DASH_EVENTS.AI_QUERY_RESULT, {
                        componentId: payload.component.id,
                        response: null,
                        success: false,
                        upgradeUrl: this._editUserUrl,
                    });
                }
            });
        }
    }
}
